// Modified from libs/core/hotels/features/shop/components/HotelShopMap

import { lazy, Suspense } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { getGoogleStaticMapSrc } from "@b2bportal/core-utilities";
import type { Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  LazyLoading,
} from "@tenant/ui";
import clsx from "clsx";
import { useHotelShopMap } from "./hooks";
import defaultStyles from "./HotelShopMap.module.scss";

export interface HotelShopMapComponentProps extends PlatformComponentProps {
  hotel: Lodging;
}

const COMPONENT_KEY = CoreHotelsComponent.HotelShopMap;

const MAP_ARGS = {
  ZOOM: 16,
  SCALE: 2,
  SHOW_MARKER: true,
  SIZE: {
    width: 600,
    height: 200,
  },
};

const LazyMap = lazy(() => import("@/components/Map"));

// Wrapper component for the map pin marker that accepts lat/lng props
// lat/lng are required by google-maps-react-markers for positioning
const MapPinMarker = ({
  lat: _lat,
  lng: _lng,
  className,
}: {
  lat: number;
  lng: number;
  className?: string;
}) => {
  return (
    <Icon iconName={IconNameEnum.hotelMapPin} className={className} alt="" />
  );
};

export const HotelShopMap = (props: HotelShopMapComponentProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const { context, text } = useHotelShopMap(props);
  const { children, className } = props;

  return (
    <div className={clsx(block, className)}>
      <img
        className={cn("static-map")}
        src={getGoogleStaticMapSrc(
          context.coordinates,
          MAP_ARGS.ZOOM,
          MAP_ARGS.SIZE,
          MAP_ARGS.SCALE,
          MAP_ARGS.SHOW_MARKER,
          "",
          context.markerIcon
        )}
        alt={t("hotelMapImageAlt", {
          hotelName: text.hotelName,
        })}
      />
      <Dialog>
        <DialogTrigger className={cn("see-map-link")}>
          {t("core-hotels.shop.location.seeMap")}
        </DialogTrigger>
        <DialogContent title={text.hotelName}>
          <div className={cn("dialog-content")}>
            <div className={cn("dialog-content-address")}>
              <Icon iconName={IconNameEnum.mapPin} />
              {text.address}
            </div>
            <div className={cn("dialog-interactive-map")}>
              <Suspense fallback={<LazyLoading />}>
                <LazyMap
                  defaultZoom={MAP_ARGS.ZOOM}
                  centroid={{
                    lat: context.coordinates.lat,
                    lng: context.coordinates.lon,
                  }}
                  points={[]}
                  className={cn("interactive-map")}
                >
                  <MapPinMarker
                    lat={context.coordinates.lat}
                    lng={context.coordinates.lon}
                    className={cn("map-pin", { interactive: true })}
                  />
                </LazyMap>
              </Suspense>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {children}
    </div>
  );
};
